/* eslint-disable no-nested-ternary */
import { Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges } from '@angular/core';
import { Fund } from '../../../fund-performance/dto/Fund';
import { PortfolioPerformance, PortfolioPerformanceDTO } from '../../../pp/dto/PortfolioPerformance';
import { HorNavbarService } from '../../../../core/service/hor-navbar.service';
import { OrganizationsService } from '../../../organization/components/service/organizations.service';
import { GradientService, GetGradientsParams } from '../../service/gradient.service';
import {
  GetPredictionsParams,
  EsopType,
  LoanConversion,
  LoanDTO,
} from '../../holding-utils';
import { firstValueFrom, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { PortfolioPerformanceBackendService } from '../../../pp/service/portfolio-performance-backend.service';
import { PredictionsService } from '../../service/predictions.service';
import { HoldingBackendService } from '../../holding-backend.service';
import { skeletonAnimations } from "../../../../util/animations/skeleton-animations";

export type CompanyHolding =
  Pick<PortfolioPerformance, 'name' | 'companyId' | 'holdingPercentage' |
  'valuationRoundValue' | 'cost' | 'fairValue' | 'calculatedGrossIrr'>;

@Component({
  selector: 'app-holding',
  templateUrl: './holding.component.html',
  styleUrls: ['./holding.component.scss'],
  animations: [skeletonAnimations],
})

export class HoldingComponent implements OnInit, OnDestroy, OnChanges {
  private readonly tableSize = 6;
  private _destroyStream: Subject<void> = new Subject<void>();
  private selectedFund: Fund = null;
  private selectedPeriod: Date = null;

  protected readonly holdingTabs = HOLDING_TABS;

  @Input() selectedCompanyName: string; // connects company from parent to company in component
  // changes ui between standalone, prediction models, and company-perf, dependent, delete after refactor finish
  @Input() isStandalone = true;
  esopType: EsopType = EsopType.pre;
  esopSelectorData = ['ESOP (Issued before Round)', 'ESOP (Issued after Round)'];
  selectedTab = HOLDING_TABS[0];
  descriptions = holdingDescriptions;
  isTarget = false;
  loanInitialData = new LoanConversion();
  /** Strings because we need <input type="text"/> to use masks on it */
  holdingBeforeRoundPercentageInput = '0';
  esopPercentageInput = '5';
  xSeries = [20000000];
  ySeries = [5000000];
  predictionsMatrix: number[][] = [];
  gradientMatrix: string[][] = [];

  companies: CompanyHolding[] = [];
  selectedCompany: CompanyHolding | null = null;
  esopPercentage: number;
  targetHolding: number;
  borderMatrix: string[][] = [];
  loanData = new LoanDTO();
  roundDetails = new RoundDetails();
  showLoadingState = true;
  protected readonly ROUND_DETAILS_PROPERTIES = ROUND_DETAILS_PROPERTIES;


  constructor(
    private predictionsService: PredictionsService,
    private gradientService: GradientService,
    private organizationsService: OrganizationsService,
    private navBarService: HorNavbarService,
    private ppService: PortfolioPerformanceBackendService,
    private holdingService: HoldingBackendService,
  ) {
    this.initRoundsAndInvestments();
    this.recalculatePredictions();
  }

  ngOnInit(): void {
    this.initSubscription();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.selectedCompanyName) {
      if (!this.selectedCompanyName || changes.selectedCompanyName.previousValue === changes.selectedCompanyName.currentValue) {
        return;
      }
      this.processSelectedCompany(this.selectedCompanyName).then(() => {});
    }
  }

  ngOnDestroy(): void {
    this._destroyStream.next();
    this._destroyStream.complete();
  }

  setEsopType(selectedEsop: string): void {
    this.esopType = selectedEsop === 'ESOP (Issued before Round)' ? EsopType.pre : EsopType.post;
    this.recalculatePredictions();
  }

  /**
   * Updates {@link roundDetails} in case value is given, fills and sets new {@link xSeries} and {@link ySeries}
   * data arrays using start values and steps from roundDetails and calls {@link recalculatePredictions()} to update
   * table values
   *
   * @param value new value to set in {@link roundDetails}
   * @param roundDetailsProperty first field of {@link roundDetails} to acccess @param field2 further
   * @param isStart boolean to determine second field of {@link roundDetails} to update and set new @param value
   *
   * E.x. {@link updateRoundsAndInvestments('12000', ROUND_DETAILS_PROPERTIES.INVESTMENT, false)} will update and set
   * {@link roundDetails.investment.step} = 12000 and update {@link ySeries} using new investment step value
   */
  updateRoundsAndInvestments(value?: string, roundDetailsProperty?: ROUND_DETAILS_PROPERTIES, isStart?: boolean): void {
    if (value === '') {
      value = '0';
    }
    if (value) {
      this.roundDetails[roundDetailsProperty][isStart ? 'startValue' : 'step'] = parseInt(value, 10);
    }

    const { tableSize } = this;
    const newXseries: number[] = [this.roundDetails[this.isTarget ?
      ROUND_DETAILS_PROPERTIES.PRE_MONEY : ROUND_DETAILS_PROPERTIES.ROUND].startValue];
    const newYseries: number[] = [this.roundDetails[this.isTarget ?
      ROUND_DETAILS_PROPERTIES.ROUND : ROUND_DETAILS_PROPERTIES.INVESTMENT].startValue];
    const xStep = this.roundDetails[this.isTarget ? ROUND_DETAILS_PROPERTIES.PRE_MONEY : ROUND_DETAILS_PROPERTIES.ROUND].step;
    const yStep = this.roundDetails[this.isTarget ? ROUND_DETAILS_PROPERTIES.ROUND : ROUND_DETAILS_PROPERTIES.INVESTMENT].step;

    for (let i = 1; i < tableSize; i++) {
      const nextX = newXseries[i - 1] + xStep;
      const nextY = newYseries[i - 1] + yStep;
      newXseries.push(nextX);
      newYseries.push(nextY);
    }

    this.xSeries = newXseries;
    this.ySeries = newYseries;
    this.recalculatePredictions();
  }

  recalculatePredictions(): void {
    const { esopPercentageInput, esopType, holdingBeforeRoundPercentageInput, ySeries: investments, xSeries: rounds } = this;
    const holdingBeforeRoundPercentage = parseFloat(holdingBeforeRoundPercentageInput) / 100;
    const esopPercentage = parseFloat(esopPercentageInput) / 100;
    const preMoney = this.roundDetails.preMoney.startValue;
    const targetHolding = parseFloat(this.roundDetails.targetHoldingInput) / 100;
    this.esopPercentage = esopPercentage;
    this.targetHolding = targetHolding;
    const totalConvertibles = this.loanData.totalConvertibles || 0;
    const discounts = this.loanData.discounts;
    const fundsLoan = this.loanData.fundsLoan || 0;

    const params: GetPredictionsParams = {
      holdingBeforeRoundPercentage,
      esopType,
      esopPercentage,
      preMoney,
      investments,
      rounds,
      targetHolding,
      totalConvertibles,
      discounts,
      fundsLoan,
    };
    const { predictionsMatrix, min, max } = this.predictionsService.getPredictionsAndMinMax(params,
      this.isStandalone ? this.selectedTab === HOLDING_TABS[0] : this.isTarget, this.loanData.isLoan);
    this.predictionsMatrix = predictionsMatrix;

    this.recalculateGradientMatrix(min, max);
    this.borderMatrix = this.predictionsMatrix.map((row, i) => row.map((td, j) => this.getBorderRadiusClass(i, j)));
  }

  setHoldingBeforeRoundPercentage(companyChanged = false): void {
    const { selectedCompany } = this;
    const compValue = selectedCompany?.valuationRoundValue;
    const percentage = selectedCompany?.holdingPercentage * 100;
    this.holdingBeforeRoundPercentageInput = percentage.toFixed(2);

    if (companyChanged && (compValue || percentage)) {
      this.roundDetails.calculateFromCompValue(compValue, percentage);
    }

    this.updateRoundsAndInvestments();
  }

  async processSelectedCompany(companyName: string): Promise<void> {
    if (!this.companies.length) {
      return;
    }
    this.showLoadingState = true;
    const index = companyName ? this.companies.findIndex((company) => company.name === companyName) : 0;
    await this.setSelectedCompany(index);
    this.showLoadingState = false;
  }

  selectedTabEmitterResolution(selectedTab: string): void {
    this.selectedTab = selectedTab;
    this.isTarget = (selectedTab === HOLDING_TABS[0]);
    this.setHoldingBeforeRoundPercentage();
  }

  /**
   * Get loan values from backend,
   * They are based on rounds without shares ('Total shares from this class' or 'Shares owned')
   * This rounds will be collected and
   * 1) Total convertibles = SUM of 'Total round' for selected rounds
   * 2) Fund's convertibles = SUM of 'Fair value' for selected rounds
   * 3) CAP and Discount Rate = Last available amount of selected rounds
   * 4) Conversion term => Initially Within CAP
   */
  async getInitialLoanData(): Promise<void> {
    this.loanInitialData = await firstValueFrom(
      this.holdingService.getInitialLoanData(this.selectedFund.id, this.selectedCompany.companyId, this.selectedPeriod),
    );
  }

  /**
   * Used to build a matrix with classes for table corner td styles (to make corners rounded)
   */
  private getBorderRadiusClass(i: number, j: number): string {
    if (i === 0 && j === 0) {
      return 'top-left';
    }
    if (i === 5 && j === 0) {
      return 'bottom-left';
    }
    if (i === 0 && j === 5) {
      return 'top-right';
    }
    if (i === 5 && j === 5) {
      return 'bottom-right';
    }
  }

  /**
   * Generates the arrays for the investments and rounds based on step
   * and 1st round and investment.
   */
  private initRoundsAndInvestments(): void {
    const { tableSize } = this;
    const isTarget = this.selectedTab === HOLDING_TABS[0];
    const xStep = this.roundDetails[isTarget ? ROUND_DETAILS_PROPERTIES.PRE_MONEY : ROUND_DETAILS_PROPERTIES.ROUND].step;
    const yStep = this.roundDetails[isTarget ? ROUND_DETAILS_PROPERTIES.ROUND : ROUND_DETAILS_PROPERTIES.INVESTMENT].step;
    for (let i = 0; i < tableSize - 1; i++) {
      this.xSeries.push(this.xSeries[i] + xStep);
      this.ySeries.push(this.ySeries[i] + yStep);
    }
  }

  private initSubscription(): void {
    this.organizationsService.selected$.pipe(takeUntil(this._destroyStream)).subscribe((org) => {
      if (org) {
        this.selectedFund = org.selectedFund;
      }
    });
    // eslint-disable-next-line @typescript-eslint/no-misused-promises
    this.navBarService.getSelectedPeriod.pipe(takeUntil(this._destroyStream)).subscribe(async (period: Date) => {
      await this.getCompanies(period);
    });
  }

  private async getCompanies(period: Date): Promise<void> {
    if (!period || !this.selectedFund?.uuid) return;
    this.showLoadingState = true;
    this.selectedPeriod = period;
    const ppRaw = await this.fetchPortfolioPerformance();
    if (!ppRaw) return;
    // All active companies
    const newCompanies = ppRaw.reduce((acc: CompanyHolding[], p) => {
      const { name, companyId, holdingPercentage, isRealized, valuationRoundValue, cost, fairValue, calculatedGrossIrr } = p;
      if (isRealized) return acc;
      acc.push({ name, companyId, holdingPercentage, valuationRoundValue, cost, fairValue, calculatedGrossIrr } as CompanyHolding);
      return acc;
    }, []);
    newCompanies.unshift({ name: 'New Company', holdingPercentage: 0 } as CompanyHolding);
    this.companies = newCompanies;
    await this.processSelectedCompany(this.selectedCompanyName);
  }

  private recalculateGradientMatrix(min: number, max: number): void {
    const { predictionsMatrix: predictions } = this;
    const getGradientsParams: GetGradientsParams = { predictions, min, max };
    this.gradientMatrix = this.gradientService.getGradients(getGradientsParams);
  }

  private async fetchPortfolioPerformance(): Promise<PortfolioPerformanceDTO[]> {
    const { selectedFund, selectedPeriod } = this;
    // Actually this is not PP model, all fields of objects in array
    // are in snake_case. There is no typings right now for it.
    if (selectedFund && selectedPeriod) {
      const res = await this.ppService.getAll(selectedFund?.uuid, selectedPeriod);
      return res;
    }
  }

  private async setSelectedCompany(index: number): Promise<void> {
    if (!this.companies.length) {
      return;
    }
    const i = this.companies.findIndex((company) => company.name === this.selectedCompany?.name);
    this.selectedCompany = (i === -1) ? this.companies.at(index) : this.companies.at(i);
    this.setHoldingBeforeRoundPercentage(true);
    await this.getInitialLoanData();
  }
}

const DEFAULT_START_ROUND_DETAILS_VALUES = { preMoney: 20000000, round: 5000000, investment: 1250000, preMoneyFactor: 1.75 };
export class RoundDetails {
  preMoney: { startValue: number; step: number };
  round: { startValue: number; step: number };
  investment: { startValue: number; step: number };
  targetHoldingInput = '10';
  _defaultVals = DEFAULT_START_ROUND_DETAILS_VALUES;

  constructor() {
    this.preMoney = { startValue: this._defaultVals.preMoney, step: this._defaultVals.preMoney / 10 };
    this.round = { startValue: this._defaultVals.round, step: this._defaultVals.round / 10 };
    this.investment = { startValue: this._defaultVals.investment, step: this._defaultVals.investment / 10 };
  }

  /**
   * Calculates all round details using @param compValue and @param percentage
   * by multiplying them with corresponding factor constants
   */
  calculateFromCompValue(compValue: number, percentage: number): void {
    if (!compValue) {
      this.targetHoldingInput = percentage.toFixed(2);
      return;
    }
    const preMoney = rounder(this._defaultVals.preMoneyFactor * compValue);
    this.preMoney = { startValue: preMoney, step: this.preMoneyStepDependence(preMoney) };
    this.round = { startValue: this.roundDependence(preMoney), step: this.roundStepDependence(preMoney) };
    this.investment = { startValue: 0, step: this.investmentStepDependence(preMoney) };
    this.targetHoldingInput = percentage.toFixed(2);
  }

  roundDependence(preMoney: number): number {
    const x = preMoney;
    if (x <= 5 * Math.pow(10, 8)) {
      return rounder(2 * Math.pow(10, -19) * x ** 3 - 2 * Math.pow(10, -10) * x ** 2
      + 0.1672 * x + 536735);
    }
    if (x > 5 * Math.pow(10, 8)) {
      return rounder(-2 * Math.pow(10, -22) * x ** 3 + Math.pow(10, -12) * x ** 2 + 0.0463 * x + 5 * Math.pow(10, 7));
    }
  }

  roundStepDependence(preMoney: number): number {
    const x = preMoney;
    if (x <= 5 * Math.pow(10, 8)) {
      return stepRounder(9 * Math.pow(10, -20) * x ** 3 - 5 * Math.pow(10, -11) * x ** 2 + 0.0142 * x + 148613);
    }
    if (x > 5 * Math.pow(10, 8)) {
      return stepRounder(Math.pow(10, -22) * x ** 3 - 2 * Math.pow(10, -12) * x ** 2 + 0.01 * x + Math.pow(10, 6));
    }
  }

  preMoneyStepDependence(preMoney: number): number {
    const x = preMoney;
    if (x <= 5 * Math.pow(10, 8)) {
      return stepRounder(-9 * Math.pow(10, -21) * x ** 3 - 2 * Math.pow(10, -11) * x ** 2
      + 0.0304 * x + 311687);
    }
    if (x > 5 * Math.pow(10, 8)) {
      return stepRounder(4 * Math.pow(10, -23) * x ** 3 - 4 * Math.pow(10, -13) * x ** 2 + 0.0034 * x + Math.pow(10, 7));
    }
  }

  investmentStepDependence(preMoney: number): number {
    const x = preMoney;
    if (x < Math.pow(10, 6)) return 0.1 * Math.pow(10, 6);
    if (x < 4 * Math.pow(10, 6)) return 0.2 * Math.pow(10, 6);
    if (x < 30 * Math.pow(10, 6)) return 0.25 * Math.pow(10, 6);
    if (x < 100 * Math.pow(10, 6)) return 0.5 * Math.pow(10, 6);
    if (x < 500 * Math.pow(10, 6)) return Math.pow(10, 6);
    if (x < 10000 * Math.pow(10, 6)) return 2 * Math.pow(10, 6);
  }
}

const rounder = (value: number): number => {
  const MIO = Math.pow(10, 6);
  const valInMio = value / MIO;
  if (valInMio < 1) return parseInt((0.1 * Math.floor(valInMio / 0.1) * MIO).toString(), 10);
  if (valInMio < 10) return parseInt((Math.floor(valInMio) * MIO).toString(), 10);
  if (valInMio < 50) return parseInt((5 * Math.floor(valInMio / 5) * MIO).toString(), 10);
  if (valInMio < 150) return parseInt((10 * Math.floor(valInMio / 10) * MIO).toString(), 10);
  if (valInMio < 250) return parseInt((25 * Math.floor(valInMio / 25) * MIO).toString(), 10);
  if (valInMio < 3000) return parseInt((50 * Math.floor(valInMio / 50) * MIO).toString(), 10);
  if (valInMio < 5000) return parseInt((100 * Math.floor(valInMio / 100) * MIO).toString(), 10);
  return parseInt((50 * Math.floor(valInMio / 50) * MIO).toString(), 10);
};

const stepRounder = (value: number): number => {
  const MIO = Math.pow(10, 6);
  const valInMio = value / MIO;
  if (valInMio < 0.5) return parseInt((0.1 * Math.ceil(valInMio / 0.1) * MIO).toString(), 10);
  if (valInMio < 1) return parseInt((0.5 * Math.ceil(valInMio / 0.5) * MIO).toString(), 10);
  if (valInMio < 20) return parseInt((5 * Math.ceil(valInMio / 5) * MIO).toString(), 10);
  if (valInMio < 500) return parseInt((10 * Math.ceil(valInMio / 10) * MIO).toString(), 10);
  if (valInMio < 5000) return parseInt((100 * Math.ceil(valInMio / 100) * MIO).toString(), 10);
  return parseInt((50 * Math.floor(valInMio / 50) * MIO).toString(), 10);
};

const holdingDescriptions = {
  disclaimerText: 'This simulation model\'s results are solely for reference and should not be\
  viewed as absolute predictions or assurances.\ Users bear complete accountability for understanding and using these outcomes.',
  useCaseText: 'In this model you can prognose the sensitivity of your post-round holding\
  as well as fair value depending on the pre-money company value and total round size',
};

export const HOLDING_TABS: string[] = ['Target holding', 'Holding prediction'];

export const ERROR_BG = '#fdf4f2';

export enum ROUND_DETAILS_PROPERTIES {
  ROUND = 'round',
  PRE_MONEY = 'preMoney',
  INVESTMENT = 'investment',
}
